import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  CircularProgress,
  Alert,
  Collapse,
  Paper,
  Autocomplete,
  Link,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Label as LabelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import toast from 'react-hot-toast';

import { livresAPI, bibliotequesAPI, rayonnagesAPI, tagsAPI, auteursAPI, editeursAPI } from '../services/api';
import { getStatutsLectureTranslated } from '../utils/statusUtils';
import { useTagMutations } from '../hooks/useTagMutations';
import LivreCard from '../components/LivreCard/LivreCard';


function LivresPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  
  // Statuts de lecture avec traductions (avec l'option "Tous les statuts" pour les filtres)
  const statutsLecture = getStatutsLectureTranslated(t, true);
  
  // Fonction helper pour traduire les statuts de lecture
  const getReadingStatusLabel = (statusValue) => {
    if (!statusValue) return t('booksPage.readingStatus.all');
    return t(`booksPage.readingStatus.${statusValue}`, statusValue);
  };
  
  // Initialiser la page depuis l'URL ou par défaut à 1
  const [page, setPage] = useState(() => {
    const pageFromUrl = searchParams.get('page');
    return pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
  });
  const [isChangingPage, setIsChangingPage] = useState(false);
  // Nombre d'éléments par page (initialisé depuis l'URL ou par défaut à 12)
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const limitFromUrl = searchParams.get('limit');
    return limitFromUrl ? parseInt(limitFromUrl, 10) : 12;
  });
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    auteur: '',
    editeur: '',
    statut: searchParams.get('statut') || '',
    annee_min: searchParams.get('annee_min') || '',
    annee_max: searchParams.get('annee_max') || '',
    bibliotheque_id: searchParams.get('bibliotheque_id') || '',
    rayonnage_id: searchParams.get('rayonnage_id') || '',
    tag_id: searchParams.get('tag_id') || '',
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, livre: null });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const showFilters = Boolean(filterAnchorEl);
  const [auteurSearchTerm, setAuteurSearchTerm] = useState('');
  const [editeurSearchTerm, setEditeurSearchTerm] = useState('');
  const [bulkTagDialog, setBulkTagDialog] = useState({ open: false, selectedTag: null, useSelectedBooks: false });
  const [bulkMoveDialog, setBulkMoveDialog] = useState({ open: false, bibliotheque_id: '', rayonnage_id: '' });
  
  // États pour la sélection
  const [selectedBookIds, setSelectedBookIds] = useState(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);

  // Fonction pour mettre à jour l'URL avec les paramètres de filtrage
  const updateURLWithFilters = useCallback((newFilters, preservePage = false) => {
    const params = new URLSearchParams();
    
    // Ajouter seulement les paramètres non-vides
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.auteur) params.set('auteur_id', newFilters.auteur);
    if (newFilters.editeur) params.set('editeur_id', newFilters.editeur);
    if (newFilters.statut) params.set('statut', newFilters.statut);
    if (newFilters.annee_min) params.set('annee_min', newFilters.annee_min);
    if (newFilters.annee_max) params.set('annee_max', newFilters.annee_max);
    if (newFilters.bibliotheque_id) params.set('bibliotheque_id', newFilters.bibliotheque_id);
    if (newFilters.rayonnage_id) params.set('rayonnage_id', newFilters.rayonnage_id);
    if (newFilters.tag_id) params.set('tag_id', newFilters.tag_id);
    
    // Préserver le paramètre page si demandé
    if (preservePage) {
      const currentPage = searchParams.get('page');
      if (currentPage) {
        params.set('page', currentPage);
      }
    }
    
    // Mettre à jour l'URL sans recharger la page
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    
    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fonction pour nettoyer l'URL de tous les paramètres de filtrage
  const clearURLFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Charger les filtres mémorisés au montage (seulement si pas de paramètres dans l'URL)
  // Utiliser un ref pour s'assurer qu'on ne le fait qu'une seule fois
  const hasLoadedFromStorageRef = React.useRef(false);
  React.useEffect(() => {
    // Ne charger depuis localStorage qu'une seule fois au montage
    if (hasLoadedFromStorageRef.current) return;
    
    // Vérifier s'il y a des paramètres dans l'URL
    const hasUrlParams = searchParams.get('statut') || searchParams.get('search') || 
                         searchParams.get('bibliotheque_id') || searchParams.get('rayonnage_id') || 
                         searchParams.get('tag_id') || searchParams.get('auteur_id') || 
                         searchParams.get('editeur_id') || searchParams.get('annee_min') || 
                         searchParams.get('annee_max');
    
    // Ne charger depuis localStorage que s'il n'y a pas de paramètres dans l'URL
    if (!hasUrlParams) {
      const savedFilters = localStorage.getItem('livresFilters');
      if (savedFilters) {
        try {
          const parsedFilters = JSON.parse(savedFilters);
          setFilters(parsedFilters);
        } catch (error) {
          console.error('Erreur lors du chargement des filtres mémorisés:', error);
          localStorage.removeItem('livresFilters');
        }
      }
    }
    hasLoadedFromStorageRef.current = true;
  }, []); // Dépendances vides pour ne s'exécuter qu'une fois

  // Sauvegarder les filtres automatiquement à chaque modification
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('livresFilters', JSON.stringify(filters));
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const { control, watch, setValue } = useForm({
    defaultValues: filters
  });

  // Requête pour récupérer l'auteur spécifique si auteur_id est dans l'URL
  const auteurIdFromUrl = searchParams.get('auteur_id');
  const { data: auteurData } = useQuery(
    ['auteur', auteurIdFromUrl],
    () => auteurIdFromUrl ? auteursAPI.getById(auteurIdFromUrl).then(res => res.data.data) : null,
    { 
      enabled: !!auteurIdFromUrl,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Requête pour récupérer l'auteur sélectionné dans le filtre
  const selectedAuteurId = watch('auteur');
  const { data: selectedAuteurData } = useQuery(
    ['auteur', selectedAuteurId],
    () => selectedAuteurId ? auteursAPI.getById(selectedAuteurId).then(res => res.data.data) : null,
    { 
      enabled: !!selectedAuteurId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Requête pour récupérer l'éditeur sélectionné dans le filtre
  const selectedEditeurId = watch('editeur');
  const { data: selectedEditeurData } = useQuery(
    ['editeur', selectedEditeurId],
    () => selectedEditeurId ? editeursAPI.getById(selectedEditeurId).then(res => res.data.data) : null,
    { 
      enabled: !!selectedEditeurId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Requête pour récupérer le rayonnage spécifique si rayonnage_id est dans l'URL
  const rayonnageIdFromUrl = searchParams.get('rayonnage_id');
  const { data: rayonnageData } = useQuery(
    ['rayonnage', rayonnageIdFromUrl],
    () => rayonnageIdFromUrl ? rayonnagesAPI.getById(rayonnageIdFromUrl).then(res => res.data.data) : null,
    { 
      enabled: !!rayonnageIdFromUrl,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Quand les données du rayonnage sont chargées, préremplir la bibliothèque
  React.useEffect(() => {
    if (rayonnageData && rayonnageIdFromUrl && !searchParams.get('bibliotheque_id')) {
      const currentBibliothequeId = watch('bibliotheque_id');
      if (!currentBibliothequeId && rayonnageData.bibliotheque_id) {
        setValue('bibliotheque_id', rayonnageData.bibliotheque_id);
        setFilters(prev => ({
          ...prev,
          bibliotheque_id: rayonnageData.bibliotheque_id
        }));
      }
    }
  }, [rayonnageData, rayonnageIdFromUrl, searchParams, setValue, watch]);

  // Gérer les paramètres URL spécifiques (remplacent les filtres mémorisés)
  // Utiliser un ref pour éviter les oscillations
  const lastProcessedUrlRef = React.useRef('');
  React.useEffect(() => {
    const currentUrl = searchParams.toString();
    
    // Si l'URL n'a pas changé, ne rien faire
    if (currentUrl === lastProcessedUrlRef.current) {
      return;
    }
    
    const bibliothequeIdFromUrl = searchParams.get('bibliotheque_id');
    const rayonnageIdFromUrl = searchParams.get('rayonnage_id');
    const auteurFromUrl = searchParams.get('auteur');
    const editeurIdFromUrl = searchParams.get('editeur_id');
    const tagIdFromUrl = searchParams.get('tag_id');
    const statutFromUrl = searchParams.get('statut');
    const anneeMinFromUrl = searchParams.get('annee_min');
    const anneeMaxFromUrl = searchParams.get('annee_max');
    const pageFromUrl = searchParams.get('page');
    const searchFromUrl = searchParams.get('search');
    const limitFromUrl = searchParams.get('limit');
    
    // Si des paramètres spécifiques sont dans l'URL, ils remplacent les filtres mémorisés
    const hasSpecificFilters = bibliothequeIdFromUrl || rayonnageIdFromUrl || auteurIdFromUrl || auteurFromUrl || editeurIdFromUrl || tagIdFromUrl || searchFromUrl || statutFromUrl || anneeMinFromUrl || anneeMaxFromUrl;
    
    // Vérifier si les filtres actuels correspondent déjà à l'URL pour éviter les oscillations
    const currentFiltersMatchUrl = 
      (bibliothequeIdFromUrl || '') === (filters.bibliotheque_id || '') &&
      (rayonnageIdFromUrl || '') === (filters.rayonnage_id || '') &&
      (auteurIdFromUrl || '') === (filters.auteur || '') &&
      (editeurIdFromUrl || '') === (filters.editeur || '') &&
      (tagIdFromUrl || '') === (filters.tag_id || '') &&
      (searchFromUrl || '') === (filters.search || '') &&
      (statutFromUrl || '') === (filters.statut || '') &&
      (anneeMinFromUrl || '') === (filters.annee_min || '') &&
      (anneeMaxFromUrl || '') === (filters.annee_max || '');
    
    if (hasSpecificFilters && !currentFiltersMatchUrl) {
      // Marquer qu'on est en train de mettre à jour depuis l'URL
      isUpdatingFromUrlRef.current = true;
      
      // Réinitialiser les filtres et appliquer seulement les filtres spécifiques
      const specificFilters = {
        search: searchFromUrl || '',
        statut: statutFromUrl || '',
        annee_min: anneeMinFromUrl || '',
        annee_max: anneeMaxFromUrl || '',
        bibliotheque_id: '',
        rayonnage_id: '',
        tag_id: '',
        auteur: '',
        editeur: ''
      };
      
      if (bibliothequeIdFromUrl) {
        specificFilters.bibliotheque_id = bibliothequeIdFromUrl;
        setValue('bibliotheque_id', bibliothequeIdFromUrl);
      }

      if (rayonnageIdFromUrl) {
        specificFilters.rayonnage_id = rayonnageIdFromUrl;
        setValue('rayonnage_id', rayonnageIdFromUrl);
      }
      
      if (auteurIdFromUrl) {
        specificFilters.auteur = auteurIdFromUrl;
        setValue('auteur', auteurIdFromUrl);
      } else if (auteurFromUrl) {
        // Fallback pour l'ancien paramètre auteur (recherche générale)
        specificFilters.search = auteurFromUrl;
        setValue('search', auteurFromUrl);
      }
      
      if (editeurIdFromUrl) {
        specificFilters.editeur = editeurIdFromUrl;
        setValue('editeur', editeurIdFromUrl);
      }
      
      if (tagIdFromUrl) {
        specificFilters.tag_id = tagIdFromUrl;
        setValue('tag_id', tagIdFromUrl);
      }
      
      if (statutFromUrl) {
        specificFilters.statut = statutFromUrl;
        setValue('statut', statutFromUrl);
      }
      
      if (anneeMinFromUrl) {
        specificFilters.annee_min = anneeMinFromUrl;
        setValue('annee_min', anneeMinFromUrl);
      }
      
      if (anneeMaxFromUrl) {
        specificFilters.annee_max = anneeMaxFromUrl;
        setValue('annee_max', anneeMaxFromUrl);
      }
      
      setFilters(specificFilters);
      
      // Si on a une page dans l'URL, l'utiliser, sinon remettre à 1
      if (pageFromUrl) {
        const pageNumber = parseInt(pageFromUrl, 10);
        setPage(pageNumber);
      } else {
        setPage(1);
      }
      
      // Marquer cette URL comme traitée et réinitialiser le flag après un court délai
      lastProcessedUrlRef.current = currentUrl;
      setTimeout(() => {
        isUpdatingFromUrlRef.current = false;
      }, 100);
    } else if (pageFromUrl && !hasSpecificFilters) {
      // Si on a seulement un changement de page (pas de filtres), juste mettre à jour la page
      const pageNumber = parseInt(pageFromUrl, 10);
      if (pageNumber !== page) {
        setPage(pageNumber);
      }
      lastProcessedUrlRef.current = currentUrl;
    }
    
    // Gérer le paramètre limit
    if (limitFromUrl) {
      const limitNumber = parseInt(limitFromUrl, 10);
      if (limitNumber !== itemsPerPage) {
        setItemsPerPage(limitNumber);
      }
    }
  }, [searchParams, setValue, filters, page, itemsPerPage, auteurIdFromUrl]);

  const watchedFilters = watch();

  // Debounce pour la recherche
  const debouncedSearch = useCallback(
    debounce((newFilters) => {
      setFilters(newFilters);
      // Mettre à jour l'URL avec les nouveaux filtres et supprimer le paramètre page
      updateURLWithFilters(newFilters);
      // Ne remettre à la page 1 que si les filtres ont vraiment changé
      setPage(1);
    }, 500),
    [updateURLWithFilters]
  );

  // Flag pour indiquer qu'on est en train de mettre à jour depuis l'URL
  const isUpdatingFromUrlRef = React.useRef(false);
  
  // Mettre à jour les filtres quand les champs changent
  React.useEffect(() => {
    console.log('🔄 Filtres surveillés changés:', watchedFilters);
    
    // Ignorer les changements si on est en train de changer de page
    if (isChangingPage) {
      console.log('⏭️ Changement de page en cours, ignorer les changements de filtres');
      return;
    }
    
    // Ignorer les changements si on est en train de mettre à jour depuis l'URL
    if (isUpdatingFromUrlRef.current) {
      console.log('⏭️ Mise à jour depuis l\'URL en cours, ignorer les changements de filtres');
      return;
    }
    
    // Vérifier si les filtres ont vraiment changé par rapport à l'état actuel
    const filtersChanged = Object.keys(watchedFilters).some(key => 
      watchedFilters[key] !== filters[key]
    );
    
    if (filtersChanged) {
      debouncedSearch(watchedFilters);
    }
  }, [watchedFilters, debouncedSearch, filters, isChangingPage]);

  // Requête pour récupérer les bibliothèques
  const { data: bibliothequesData } = useQuery(
    'bibliotheques-list',
    () => bibliotequesAPI.getAll().then(res => res.data.data),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000 }
  );

  // Requête pour récupérer les rayonnages
  const { data: rayonnagesData } = useQuery(
    'rayonnages-list',
    () => rayonnagesAPI.getAll().then(res => res.data.data),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000 }
  );

  // Requête pour récupérer les tags
  const { data: tagsData, isLoading: tagsLoading } = useQuery(
    'tags-list',
    () => tagsAPI.getAll().then(res => res.data.data),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000 }
  );

  // Requête pour récupérer les auteurs avec recherche incrémentale
  const { data: auteursData, isLoading: auteursLoading } = useQuery(
    ['auteurs-list', auteurSearchTerm],
    () => auteursAPI.search(auteurSearchTerm).then(res => res.data.data),
    { 
      staleTime: 2 * 60 * 1000, 
      cacheTime: 5 * 60 * 1000,
      enabled: auteurSearchTerm.length >= 2 || auteurSearchTerm.length === 0
    }
  );

  // Requête pour récupérer les éditeurs avec recherche incrémentale
  const { data: editeursData, isLoading: editeursLoading } = useQuery(
    ['editeurs-list', editeurSearchTerm],
    () => editeursAPI.search(editeurSearchTerm).then(res => res.data.data),
    { 
      staleTime: 2 * 60 * 1000, 
      cacheTime: 5 * 60 * 1000,
      enabled: editeurSearchTerm.length >= 2 || editeurSearchTerm.length === 0
    }
  );

  // Requête pour récupérer les livres
  const { data, isLoading, error } = useQuery(
    ['livres', page, itemsPerPage, filters],
    () => {
      console.log('🔍 Requête livres avec filtres:', { page, limit: itemsPerPage, ...filters });
      return livresAPI.getAll({ page, limit: itemsPerPage, ...filters }).then(res => {
        console.log('📚 Réponse API livres:', res.data);
        return res.data;
      });
    },
    {
      keepPreviousData: true, // Garder les données précédentes pour éviter les flashs
      staleTime: 5 * 60 * 1000, // Cache de 5 minutes
    }
  );

  // Mutation pour supprimer un livre
  const deleteLivreMutation = useMutation(
    (id) => livresAPI.delete(id),
    {
      onSuccess: () => {
        toast.success(t('booksPage.deleteSuccess'));
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('tags-list');
        queryClient.invalidateQueries('auteurs-list');
        queryClient.invalidateQueries('editeurs-list');
        setDeleteDialog({ open: false, livre: null });
      },
      onError: (error) => {
        toast.error(t('booksPage.deleteError'));
        console.error('Erreur:', error);
      }
    }
  );

  // Mutation pour appliquer un tag à tous les livres filtrés
  const bulkTagMutation = useMutation(
    ({ tagId, filters }) => {
      return livresAPI.bulkAddTag(tagId, filters);
    },
    {
      onSuccess: (response) => {
        const count = response.data.count || 0;
        toast.success(t('booksPage.applyTagSuccess', { count }));
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('tags-list');
        setBulkTagDialog({ open: false, selectedTag: null });
      },
      onError: (error) => {
        toast.error(t('booksPage.applyTagError'));
        console.error('Erreur:', error);
      }
    }
  );

  // Hook personnalisé pour les mutations de tags
  const getLivreById = (livreId) => data?.data?.find(livre => livre.id === livreId);
  const { addTagMutation, removeTagMutation, isTagLoading } = useTagMutations(null, tagsData, getLivreById);

  // Mutation pour appliquer un tag aux livres sélectionnés
  const bulkTagSelectedMutation = useMutation(
    ({ tagId, bookIds }) => {
      return livresAPI.bulkAddTagByIds(tagId, bookIds);
    },
    {
      onSuccess: (response) => {
        const count = response.data.count || 0;
        toast.success(t('booksPage.applyTagSuccess', { count }));
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('tags-list');
        setSelectedBookIds(new Set()); // Vider la sélection après l'action
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || t('booksPage.applyTagError'));
        console.error('Erreur:', error);
      }
    }
  );

  // Mutation pour déplacer les livres sélectionnés vers un rayonnage
  const bulkMoveMutation = useMutation(
    ({ livreIds, rayonnage_id, motif }) => {
      return livresAPI.bulkMoveToRayonnage(livreIds, rayonnage_id, motif);
    },
    {
      onSuccess: (response) => {
        const count = response.data.count || 0;
        toast.success(t('booksPage.moveSuccess', { count }));
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('bibliotheques-list');
        queryClient.invalidateQueries('rayonnages-list');
        setSelectedBookIds(new Set()); // Vider la sélection après l'action
        setBulkMoveDialog({ open: false, bibliotheque_id: '', rayonnage_id: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || t('booksPage.moveError'));
        console.error('Erreur:', error);
      }
    }
  );

  const handlePageChange = (event, newPage) => {
    setIsChangingPage(true);
    setPage(newPage);
    // Mettre à jour l'URL avec le nouveau numéro de page
    const newSearchParams = new URLSearchParams(searchParams);
    if (newPage === 1) {
      newSearchParams.delete('page'); // Supprimer le paramètre page si c'est la page 1
    } else {
      newSearchParams.set('page', newPage.toString());
    }
    setSearchParams(newSearchParams);
    // Réinitialiser le flag après un court délai
    setTimeout(() => setIsChangingPage(false), 100);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setPage(1); // Retourner à la page 1 quand on change le nombre d'éléments par page
    
    // Mettre à jour l'URL
    const newSearchParams = new URLSearchParams(searchParams);
    if (newItemsPerPage === 12) {
      newSearchParams.delete('limit'); // Supprimer le paramètre limit si c'est la valeur par défaut
    } else {
      newSearchParams.set('limit', newItemsPerPage.toString());
    }
    newSearchParams.delete('page'); // Supprimer le paramètre page car on retourne à la page 1
    setSearchParams(newSearchParams);
  };

  const handleView = (id) => {
    navigate(`/livres/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/livres/${id}/modifier`);
  };

  const handleDelete = (livre) => {
    setDeleteDialog({ open: true, livre });
  };

  const confirmDelete = () => {
    if (deleteDialog.livre) {
      deleteLivreMutation.mutate(deleteDialog.livre.id);
    }
  };

  // Fonction pour obtenir les filtres actifs avec leurs labels
  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (filters.search) {
      activeFilters.push({ key: 'search', label: `${t('booksPage.searchFilter')}: "${filters.search}"`, value: filters.search });
    }
    if (filters.statut) {
      const statutLabel = getReadingStatusLabel(filters.statut);
      activeFilters.push({ key: 'statut', label: `${t('booksPage.statusFilter')}: ${statutLabel}`, value: filters.statut });
    }
    if (filters.auteur) {
      const auteur = auteursData?.find(a => a.id === parseInt(filters.auteur));
      const auteurLabel = auteur ? `${auteur.prenom || ''} ${auteur.nom}`.trim() : t('booksPage.authorSelected');
      activeFilters.push({ key: 'auteur', label: `${t('booksPage.authorFilter')}: ${auteurLabel}`, value: filters.auteur });
    }
    if (filters.editeur) {
      const editeur = editeursData?.find(e => e.id === parseInt(filters.editeur));
      const editeurLabel = editeur ? editeur.nom : t('booksPage.publisherSelected');
      activeFilters.push({ key: 'editeur', label: `${t('booksPage.publisherFilter')}: ${editeurLabel}`, value: filters.editeur });
    }
    if (filters.tag_id) {
      let tagLabel;
      if (filters.tag_id === 'sans_tag') {
        tagLabel = t('booksPage.noTag');
      } else {
        const tag = tagsData?.find(t => t.id === parseInt(filters.tag_id));
        tagLabel = `${t('booksPage.tag')} : ${tag ? tag.nom : t('booksPage.tagSelected')}`;
      }
      activeFilters.push({ key: 'tag_id', label: tagLabel, value: filters.tag_id });
    }
    if (filters.bibliotheque_id) {
      const biblio = bibliothequesData?.find(b => b.id === parseInt(filters.bibliotheque_id));
      const biblioLabel = biblio ? biblio.nom : t('booksPage.librarySelected');
      activeFilters.push({ key: 'bibliotheque_id', label: `${t('booksPage.libraryFilter')}: ${biblioLabel}`, value: filters.bibliotheque_id });
    }
    if (filters.rayonnage_id) {
      const rayonnage = rayonnagesData?.find(r => r.id === parseInt(filters.rayonnage_id));
      const rayonnageLabel = rayonnage ? rayonnage.nom : t('booksPage.shelfSelected');
      activeFilters.push({ key: 'rayonnage_id', label: `${t('booksPage.shelfFilter')}: ${rayonnageLabel}`, value: filters.rayonnage_id });
    }
    if (filters.annee_min || filters.annee_max) {
      const yearRange = filters.annee_min && filters.annee_max 
        ? `${filters.annee_min}${t('booksPage.yearRange')}${filters.annee_max}`
        : filters.annee_min 
          ? `${t('booksPage.yearFrom')} ${filters.annee_min}`
          : `${t('booksPage.yearTo')} ${filters.annee_max}`;
      activeFilters.push({ key: 'annee', label: `${t('booksPage.yearFilter')}: ${yearRange}`, value: 'annee' });
    }
    
    return activeFilters;
  };

  const removeFilter = (filterKey) => {
    if (filterKey === 'annee') {
      setValue('annee_min', '');
      setValue('annee_max', '');
      setFilters({ ...filters, annee_min: '', annee_max: '' });
    } else {
      setValue(filterKey, '');
      setFilters({ ...filters, [filterKey]: '' });
    }
    setPage(1);
    // Mettre à jour l'URL avec les filtres modifiés
    updateURLWithFilters({ ...filters, [filterKey]: '' });
  };

  const resetFilters = () => {
    const defaultFilters = { 
      search: '', 
      statut: '', 
      annee_min: '', 
      annee_max: '', 
      bibliotheque_id: '', 
      rayonnage_id: '', 
      tag_id: '',
      auteur: '',
      editeur: ''
    };
    
    // Mettre à jour les valeurs du formulaire
    Object.entries(defaultFilters).forEach(([key, value]) => {
      setValue(key, value);
    });
    
    // Mettre à jour l'état des filtres
    setFilters(defaultFilters);
    
    // Nettoyer l'URL des paramètres de filtrage
    clearURLFilters();
    
    // Réinitialiser la page
    setPage(1);
  };

  const handleBulkTag = () => {
    setBulkTagDialog({ open: true, selectedTag: null });
  };

  const handleAuthorClick = (authorId) => {
    console.log('🔍 Clic sur auteur ID:', authorId);
    
    if (authorId) {
      // Naviguer vers la fiche de l'auteur
      navigate(`/auteurs/${authorId}`);
      console.log('✅ Navigation vers la fiche auteur:', authorId);
    } else {
      console.log('❌ ID auteur manquant');
    }
  };

  const handleEditeurClick = (editeurId) => {
    console.log('🔍 Clic sur éditeur ID:', editeurId);
    
    if (editeurId) {
      // Naviguer vers la fiche de l'éditeur
      navigate(`/editeurs/${editeurId}`);
      console.log('✅ Navigation vers la fiche éditeur:', editeurId);
    } else {
      console.log('❌ ID éditeur manquant');
    }
  };

  const handlePhotoClick = (livreId) => {
    console.log('🖼️ Clic sur photo du livre ID:', livreId);
    
    if (livreId) {
      // Naviguer vers la fiche du livre
      navigate(`/livres/${livreId}`);
    } else {
      console.log('❌ ID livre manquant');
    }
  };

  const confirmBulkTag = () => {
    if (bulkTagDialog.selectedTag) {
      if (bulkTagDialog.useSelectedBooks && selectedBookIds.size > 0) {
        // Utiliser les livres sélectionnés
        const bookIdsArray = Array.from(selectedBookIds);
        bulkTagSelectedMutation.mutate({ 
          tagId: bulkTagDialog.selectedTag.id, 
          bookIds: bookIdsArray 
        });
      } else {
        // Utiliser les filtres (comportement original)
        const currentFilters = {
          search: filters.search,
          statut: filters.statut,
          annee_min: filters.annee_min,
          annee_max: filters.annee_max,
          bibliotheque_id: filters.bibliotheque_id,
          rayonnage_id: filters.rayonnage_id,
          tag_id: filters.tag_id,
          auteur: filters.auteur,
          editeur: filters.editeur
        };
        
        // Nettoyer les filtres vides
        const cleanFilters = Object.fromEntries(
          Object.entries(currentFilters).filter(([_, value]) => value !== '')
        );
        
        bulkTagMutation.mutate({ 
          tagId: bulkTagDialog.selectedTag.id, 
          filters: cleanFilters
        });
      }
      setBulkTagDialog({ open: false, selectedTag: null, useSelectedBooks: false });
    }
  };

  // Fonctions de gestion de sélection
  const handleToggleSelection = (bookId) => {
    setSelectedBookIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookId)) {
        newSet.delete(bookId);
      } else {
        newSet.add(bookId);
      }
      return newSet;
    });
  };

  const handleSelectAll = async () => {
    setIsSelectingAll(true);
    try {
      // Récupérer tous les IDs des livres correspondant aux filtres actuels
      const currentFilters = {
        search: filters.search,
        statut: filters.statut,
        annee_min: filters.annee_min,
        annee_max: filters.annee_max,
        bibliotheque_id: filters.bibliotheque_id,
        rayonnage_id: filters.rayonnage_id,
        tag_id: filters.tag_id,
        auteur: filters.auteur,
        editeur: filters.editeur
      };
      
      // Nettoyer les filtres vides
      const cleanFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, value]) => value !== '')
      );
      
      // Récupérer le total de livres d'abord
      const firstPageResponse = await livresAPI.getAll({ 
        page: 1, 
        limit: 100, // Limite maximale autorisée par le backend
        ...cleanFilters 
      });
      
      const totalBooks = firstPageResponse.data.pagination.total;
      const totalPages = firstPageResponse.data.pagination.totalPages;
      const allBookIds = new Set(firstPageResponse.data.data.map(livre => livre.id));
      
      // Si il y a plus d'une page, récupérer les autres pages
      if (totalPages > 1) {
        const promises = [];
        for (let page = 2; page <= totalPages; page++) {
          promises.push(
            livresAPI.getAll({ 
              page, 
              limit: 100,
              ...cleanFilters 
            })
          );
        }
        
        const responses = await Promise.all(promises);
        responses.forEach(response => {
          response.data.data.forEach(livre => {
            allBookIds.add(livre.id);
          });
        });
      }
      
      setSelectedBookIds(allBookIds);
      toast.success(t('booksPage.selectAllSuccess', { count: allBookIds.size }));
    } catch (error) {
      console.error('Erreur lors de la sélection de tous les livres:', error);
      toast.error(t('booksPage.selectAllError'));
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectedBookIds(new Set());
    toast.success(t('booksPage.deselectSuccess'));
  };

  // Nettoyer la sélection quand les filtres changent
  React.useEffect(() => {
    // Vider la sélection quand les filtres changent (recherche, auteur, éditeur, etc.)
    // car les livres sélectionnés peuvent ne plus correspondre aux nouveaux filtres
    setSelectedBookIds(new Set());
  }, [filters]);

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {t('booksPage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: selectedBookIds.size > 0 ? 10 : 0 }}>
      {/* Barre de recherche principale */}
      <Box sx={{ mb: 3 }}>
        <Controller
          name="search"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              placeholder={t('booksPage.searchPlaceholder')}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
                sx: {
                  fontSize: '1.1rem',
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 14px 14px 0',
                },
              }}
            />
          )}
        />
      </Box>

      {/* Bouton pour afficher/masquer les filtres */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<FilterIcon sx={{ color: 'black' }} />}
          onClick={(event) => setFilterAnchorEl(event.currentTarget)}
          sx={{ 
            fontSize: '16px',
            borderRadius: 2,
            color: 'black',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            backgroundColor: 'white',
            height: '52px', // Même hauteur que le TextField
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              backgroundColor: 'background.default',
            },
          }}
        >
          {t('booksPage.filters')}
          {getActiveFilters().length > 0 && (
            <Chip 
              label={getActiveFilters().length} 
              size="small" 
              color="primary"
              sx={{ 
                ml: 1, 
                height: 20,
                minWidth: 20,
                '& .MuiChip-label': {
                  px: 0.75,
                }
              }}
            />
          )}
        </Button>
        {getActiveFilters().length > 0 && (
          <Button
            variant="text"
            size="small"
            onClick={resetFilters}
            sx={{ borderRadius: 2 }}
          >
            {t('booksPage.reset')}
          </Button>
        )}
      </Box>

      {/* Chips des filtres actifs */}
      {getActiveFilters().length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {getActiveFilters().map((filter) => (
            <Chip
              key={filter.key}
              label={filter.label}
              onDelete={() => removeFilter(filter.key)}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      )}

      {/* Popover des filtres */}
      <Popover
        open={showFilters}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2.5,
            borderRadius: 2,
            boxShadow: 4,
            minWidth: 400,
            maxWidth: 600,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FilterIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('booksPage.filters')}
            </Typography>
            {getActiveFilters().length > 0 && (
              <Chip 
                label={getActiveFilters().length} 
                size="small" 
                color="primary"
                sx={{ fontWeight: 'bold', ml: 1 }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Recherche par auteur */}
          <Box sx={{ mb: 2 }}>
            <Controller
                  name="auteur"
                  control={control}
                  render={({ field }) => {
                    // Priorité : auteur de l'URL, puis auteur sélectionné, puis recherche dans la liste
                    const selectedAuteur = auteurData || selectedAuteurData || auteursData?.find(auteur => auteur.id === field.value) || null;
                    
                    // Combiner les options : auteur de l'URL, auteur sélectionné, puis liste des auteurs
                    const allOptions = [
                      ...(auteurData ? [auteurData] : []),
                      ...(selectedAuteurData && selectedAuteurData.id !== auteurData?.id ? [selectedAuteurData] : []),
                      ...(auteursData || [])
                    ];
                    
                    // Supprimer les doublons basés sur l'ID
                    const options = allOptions.filter((option, index, self) => 
                      index === self.findIndex(o => o.id === option.id)
                    );
                    
                    return (
                      <Autocomplete
                        value={selectedAuteur}
                        options={options}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          return `${option.prenom || ''} ${option.nom}`.trim();
                        }}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return false;
                          return option.id === value.id;
                        }}
                        loading={auteursLoading}
                        loadingText={t('booksPage.authorLoading')}
                        noOptionsText={t('booksPage.authorNotFound')}
                        onInputChange={(event, newInputValue) => {
                          setAuteurSearchTerm(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue.id : '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('booksPage.author')}
                            placeholder={t('booksPage.authorPlaceholder')}
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {auteursLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            {`${option.prenom || ''} ${option.nom}`.trim()}
                          </Box>
                        )}
                      />
                    );
                  }}
            />
          </Box>
          
          {/* Recherche par éditeur */}
          <Box sx={{ mb: 2 }}>
            <Controller
            name="editeur"
            control={control}
            render={({ field }) => {
              // Priorité : éditeur sélectionné, puis recherche dans la liste
              const selectedEditeur = selectedEditeurData || editeursData?.find(editeur => editeur.id === field.value) || null;
              
              return (
                <Autocomplete
                  value={selectedEditeur}
                  options={editeursData || []}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return option.nom || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  loading={editeursLoading}
                  loadingText={t('booksPage.publisherLoading')}
                  noOptionsText={t('booksPage.publisherNotFound')}
                  onInputChange={(event, newInputValue) => {
                    setEditeurSearchTerm(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    field.onChange(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('booksPage.publisher')}
                      placeholder={t('booksPage.publisherPlaceholder')}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {editeursLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.nom}
                    </Box>
                  )}
                />
              );
            }}
            />
          </Box>
          
          {/* Bibliothèque et Rayonnage */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <Controller
              name="bibliotheque_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t('booksPage.library')}</InputLabel>
                  <Select 
                    {...field} 
                    label={t('booksPage.library')}
                    onChange={(e) => {
                      field.onChange(e);
                      // Réinitialiser le rayonnage quand la bibliothèque change
                      setValue('rayonnage_id', '');
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>{t('booksPage.allLibraries')}</em>
                    </MenuItem>
                    <MenuItem value="sans_bibliotheque">
                      <em>{t('booksPage.noLibrary')}</em>
                    </MenuItem>
                    {bibliothequesData?.map((bibliotheque) => (
                      <MenuItem key={bibliotheque.id} value={bibliotheque.id}>
                        {bibliotheque.nom}
                        {bibliotheque.piece && ` (${bibliotheque.piece})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            
            <Controller
              name="rayonnage_id"
              control={control}
              render={({ field }) => {
                const selectedBibliothequeId = watch('bibliotheque_id');
                const filteredRayonnages = rayonnagesData?.filter(rayonnage => 
                  rayonnage.bibliotheque_id === parseInt(selectedBibliothequeId)
                ) || [];
                
                return (
                  <FormControl fullWidth>
                    <InputLabel>{t('booksPage.shelf')}</InputLabel>
                    <Select 
                      {...field} 
                      label={t('booksPage.shelf')}
                      disabled={!selectedBibliothequeId}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="">
                        <em>{t('booksPage.allShelves')}</em>
                      </MenuItem>
                      {filteredRayonnages.map((rayonnage) => (
                        <MenuItem key={rayonnage.id} value={rayonnage.id}>
                          {rayonnage.nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }}
            />
          </Box>
          
          {/* Tag */}
          <Box sx={{ mb: 2 }}>
            <Controller
              name="tag_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t('booksPage.tag')}</InputLabel>
                  <Select {...field} label={t('booksPage.tag')} sx={{ borderRadius: 2 }}>
                    <MenuItem value="">
                      <em>{t('booksPage.allTags')}</em>
                    </MenuItem>
                    <MenuItem value="sans_tag">
                      <em>{t('booksPage.noTag')}</em>
                    </MenuItem>
                    {tagsData?.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: tag.couleur || '#1976d2'
                            }}
                          />
                          {tag.nom}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          
          {/* Statut et Années */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Controller
              name="statut"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t('booksPage.status')}</InputLabel>
                  <Select {...field} label={t('booksPage.status')} sx={{ borderRadius: 2 }}>
                    {statutsLecture.map((statut) => (
                      <MenuItem key={statut.value} value={statut.value}>
                        {getReadingStatusLabel(statut.value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            
            <Controller
              name="annee_min"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('booksPage.yearMin')}
                  type="number"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
            
            <Controller
              name="annee_max"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('booksPage.yearMax')}
                  type="number"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      borderRadius: 2,
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
          </Box>
          
          {/* Boutons d'action */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              onClick={resetFilters}
              disabled={getActiveFilters().length === 0}
              sx={{ borderRadius: 2 }}
            >
              {t('booksPage.reset')}
            </Button>
            <Button
              onClick={() => setFilterAnchorEl(null)}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              {t('booksPage.close')}
            </Button>
          </Box>
        </Box>
      </Popover>


      {/* Résultats */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {data?.data?.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {data?.pagination?.total || 0} {t('booksPage.booksFound')}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleSelectAll}
                disabled={isSelectingAll || selectedBookIds.size === data?.pagination?.total}
                sx={{
                  fontSize: '14px',
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                {isSelectingAll ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('booksPage.selecting')}
                  </>
                ) : selectedBookIds.size === data?.pagination?.total ? (
                  t('booksPage.allSelected')
                ) : (
                  `${t('booksPage.selectAll')} (${data?.pagination?.total || 0})`
                )}
              </Button>
            </Box>
          )}

          {data?.data?.length === 0 ? (
            <Alert severity="info">
              {t('booksPage.noBooksFound')} {filters.search || filters.statut ? t('booksPage.tryFilters') : t('booksPage.addFirstBook')}
            </Alert>
          ) : (
            <>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: {
                  xs: 'repeat(auto-fit, minmax(130px, 1fr))', 
                  sm: 'repeat(auto-fit, minmax(175px, 1fr))', 
                },
                gap: { xs: 2, md: 3 },
                width: '100%'
              }}>
                {data?.data?.map((livre) => (
                  <Box key={livre.id} sx={{ width: '100%', minWidth: 0 }}>
                    <LivreCard
                      livre={livre}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onAuthorClick={handleAuthorClick}
                      onEditeurClick={handleEditeurClick}
                      onPhotoClick={handlePhotoClick}
                      availableTags={tagsData || []}
                      onTagAdd={(livre, tagId) => addTagMutation.mutate({ livreId: livre.id, tagId })}
                      onTagRemove={(livre, tag) => removeTagMutation.mutate({ livreId: livre.id, tagId: tag.id })}
                      isTagLoading={isTagLoading}
                      queryClient={queryClient}
                      isSelected={selectedBookIds.has(livre.id)}
                      onToggleSelection={handleToggleSelection}
                    />
                  </Box>
                ))}
              </Box>

              {/* Contrôles de pagination - toujours affichés */}
              <Box sx={{ mt: 4 }}>
                {/* Contrôles de pagination */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  {/* Sélecteur d'éléments par page */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('booksPage.booksPerPage')}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        displayEmpty
                      >
                        <MenuItem value={12}>12</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Informations de pagination */}
                  <Typography variant="body2" color="text.secondary">
                    {t('booksPage.page')} {page} {t('booksPage.of')} {data?.pagination?.totalPages || 1} 
                    &nbsp;({data?.pagination?.total || 0} {t('homePage.books')} {t('booksPage.total')})
                  </Typography>
                </Box>

                {/* Navigation de pages - seulement si plus d'une page */}
                {data?.pagination?.totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={data.pagination.totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      siblingCount={2}
                      boundaryCount={1}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, livre: null })}
      >
        <DialogTitle>{t('booksPage.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('booksPage.deleteConfirm', { title: deleteDialog.livre?.titre })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('booksPage.deleteIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, livre: null })}>
            {t('booksPage.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteLivreMutation.isLoading}
          >
            {deleteLivreMutation.isLoading ? t('booksPage.deleting') : t('booksPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour déplacer les livres sélectionnés */}
      <Dialog 
        open={bulkMoveDialog.open} 
        onClose={() => setBulkMoveDialog({ open: false, bibliotheque_id: '', rayonnage_id: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArrowForwardIcon color="primary" />
            {t('booksPage.moveSelected')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }} dangerouslySetInnerHTML={{
            __html: t('booksPage.moveConfirm', { count: selectedBookIds.size })
          }} />
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('booksPage.library')}</InputLabel>
              <Select 
                value={bulkMoveDialog.bibliotheque_id}
                label={t('booksPage.library')}
                onChange={(e) => {
                  setBulkMoveDialog(prev => ({ ...prev, bibliotheque_id: e.target.value, rayonnage_id: '' }));
                }}
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="">
                  <em>{t('booksPage.noLibraryOption')}</em>
                </MenuItem>
                {bibliothequesData?.map((bibliotheque) => (
                  <MenuItem key={bibliotheque.id} value={bibliotheque.id}>
                    {bibliotheque.nom}
                    {bibliotheque.piece && ` (${bibliotheque.piece})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>{t('booksPage.shelf')}</InputLabel>
              <Select 
                value={bulkMoveDialog.rayonnage_id}
                label={t('booksPage.shelf')}
                disabled={!bulkMoveDialog.bibliotheque_id}
                onChange={(e) => {
                  setBulkMoveDialog(prev => ({ ...prev, rayonnage_id: e.target.value }));
                }}
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="">
                  <em>{t('booksPage.noShelf')}</em>
                </MenuItem>
                {rayonnagesData?.filter(rayonnage => 
                  rayonnage.bibliotheque_id === parseInt(bulkMoveDialog.bibliotheque_id)
                ).map((rayonnage) => (
                  <MenuItem key={rayonnage.id} value={rayonnage.id}>
                    {rayonnage.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkMoveDialog({ open: false, bibliotheque_id: '', rayonnage_id: '' })}>
            {t('booksPage.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (bulkMoveDialog.rayonnage_id) {
                const bookIdsArray = Array.from(selectedBookIds);
                bulkMoveMutation.mutate({ 
                  livreIds: bookIdsArray, 
                  rayonnage_id: parseInt(bulkMoveDialog.rayonnage_id),
                  motif: 'Déplacement en masse'
                });
              } else {
                toast.error(t('booksPage.selectRayonnage'));
              }
            }}
            color="primary"
            disabled={!bulkMoveDialog.rayonnage_id || bulkMoveMutation.isLoading}
          >
            {bulkMoveMutation.isLoading ? t('booksPage.moving') : t('booksPage.move')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour appliquer un tag en masse */}
      <Dialog 
        open={bulkTagDialog.open} 
        onClose={() => setBulkTagDialog({ open: false, selectedTag: null, useSelectedBooks: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon color="primary" />
            {t('booksPage.applyTag')} {bulkTagDialog.useSelectedBooks ? t('booksPage.applyTagToSelected') : t('booksPage.applyTagToAll')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }} dangerouslySetInnerHTML={{
            __html: t('booksPage.applyTagConfirm', { 
              count: bulkTagDialog.useSelectedBooks ? selectedBookIds.size : (data?.pagination?.total || 0),
              context: bulkTagDialog.useSelectedBooks ? t('booksPage.selected') : t('booksPage.matchingFilters')
            })
          }} />
          
          <Autocomplete
            value={bulkTagDialog.selectedTag}
            options={tagsData || []}
            getOptionLabel={(option) => option.nom || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={tagsLoading}
            loadingText={t('booksPage.tagLoading')}
            noOptionsText={t('booksPage.tagNotFound')}
            onChange={(event, newValue) => {
              setBulkTagDialog(prev => ({ ...prev, selectedTag: newValue }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('booksPage.tagSelectPlaceholder')}
                placeholder={t('booksPage.tagSelectSearch')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {tagsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: option.couleur,
                    }}
                  />
                  {option.nom}
                </Box>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkTagDialog({ open: false, selectedTag: null, useSelectedBooks: false })}>
            {t('booksPage.cancel')}
          </Button>
          <Button
            onClick={confirmBulkTag}
            color="primary"
            disabled={!bulkTagDialog.selectedTag || bulkTagMutation.isLoading || bulkTagSelectedMutation.isLoading}
          >
            {(bulkTagMutation.isLoading || bulkTagSelectedMutation.isLoading) ? t('booksPage.applying') : t('booksPage.applyTagButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barre d'action flottante pour les livres sélectionnés */}
      {selectedBookIds.size > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            pb: 2,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: '16px',
              px: 2.5,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: 'white',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
              maxWidth: 'fit-content',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
              {selectedBookIds.size} {t('booksPage.booksSelected')}
            </Typography>
            
            <Box
              sx={{
                width: '1px',
                height: '20px',
                backgroundColor: 'divider',
              }}
            />
            
            <Button
              variant="contained"
              startIcon={<MoreVertIcon />}
              size="small"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 2,
                py: 0.75,
              }}
              onClick={(event) => {
                setActionsMenuAnchor(event.currentTarget);
              }}
            >
              {t('booksPage.actions')}
            </Button>
            
            <IconButton
              size="small"
              onClick={handleDeselectAll}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
      )}

      {/* Menu d'actions en masse */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={() => setActionsMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: -1,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <MenuItem
          onClick={() => {
            setActionsMenuAnchor(null);
            setBulkTagDialog({ open: true, selectedTag: null, useSelectedBooks: true });
          }}
        >
          <ListItemIcon>
            <LabelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('booksPage.addTag')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionsMenuAnchor(null);
            setBulkMoveDialog({ open: true, bibliotheque_id: '', rayonnage_id: '' });
          }}
        >
          <ListItemIcon>
            <ArrowForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('booksPage.move')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default LivresPage;
